import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';

export type CardSizeLabels = Readonly<{
  cocktail: string;
  luncheon: string;
  postcard4x6: string;
  magnet4x6: string;
}>;

// Untranslated fallback for callers outside a React/i18n context (and tests).
const DEFAULT_LABELS: CardSizeLabels = {
  cocktail: 'Cocktail',
  luncheon: 'Luncheon',
  postcard4x6: 'Postcard 4x6',
  magnet4x6: 'Magnet 4x6',
};

// Translated labels for named card sizes. The tq calls live here — directly in a
// hook with `useTranslation` in scope — so translation extraction can trace them;
// do not move them into `formatCardSizeName` (the extractor cannot follow
// function parameters).
export const useCardSizeLabels = (): CardSizeLabels => {
  const { tq } = useTranslation('stationery');
  return {
    cocktail: tq`Cocktail`,
    luncheon: tq`Luncheon`,
    postcard4x6: tq`Postcard 4x6`,
    magnet4x6: tq`Magnet 4x6`,
  };
};

// 4x6/6x4 are the physical postcard dimensions; the label is orientation-agnostic
// because a 6x4 landscape card is the same physical postcard rotated (orientation
// has its own picker).
export const isPostcardCardSize = (widthInches: number, heightInches: number): boolean => (widthInches === 4 && heightInches === 6) || (widthInches === 6 && heightInches === 4);

// Categories Joy sells as postcards. Both thank-you and save-the-date ship at
// postcard dimensions with a hand-addressed mailing back and no envelope; every
// other category at 4x6/6x4 would be a template-author mistake, so postcard-ness
// is category AND size. Mirrors card_service's POSTCARD_CATEGORIES / isPostcard.
const POSTCARD_CATEGORIES: ReadonlySet<StationeryTemplateCategoryEnum> = new Set([StationeryTemplateCategoryEnum.thankYou, StationeryTemplateCategoryEnum.saveTheDate]);

export const isPostcardCategory = (category: StationeryTemplateCategoryEnum | undefined): boolean => !!category && POSTCARD_CATEGORIES.has(category);

// A template is a postcard when it is a postcard category at postcard dimensions.
export const isPostcard = (category: StationeryTemplateCategoryEnum | undefined, widthInches: number, heightInches: number): boolean =>
  isPostcardCategory(category) && isPostcardCardSize(widthInches, heightInches);

/**
 * Customer-facing name for a card size. Named sizes (napkins, postcards) read as
 * products; everything else falls back to measurements. Pass labels from
 * `useCardSizeLabels()` where a React context is available.
 */
export const formatCardSizeName = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels = DEFAULT_LABELS,
  productType?: string,
): string => {
  // The magnet PRODUCT (MAGNET4X6) shares the saveTheDate postcard templates,
  // so the product — not category/size — decides its name.
  if (productType === 'magnet' && isPostcardCardSize(widthInches, heightInches)) {
    return labels.magnet4x6;
  }
  if (category === StationeryTemplateCategoryEnum.napkin) {
    if (widthInches === 4.75) return labels.cocktail;
    if (widthInches === 6.5) return labels.luncheon;
  }
  if (isPostcard(category, widthInches, heightInches)) {
    return labels.postcard4x6;
  }
  return `${widthInches} × ${heightInches} inches`;
};
